// ─────────────────────────────────────────────────────────────────────────────
// OnboardingWizard
//
// Shown once on first login when profiles.onboarding_completed is false/null.
// Full-screen overlay, 4 steps:
//   1. Welcome — what Futures OS does
//   2. Create first account — firm, type, balance, split
//   3. How to log trades — manual vs CSV
//   4. Ready — links to Trade Entry and Guide
//
// Skippable at any step. On finish or skip, sets onboarding_completed: true.
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from "react";
import { Btn } from "../../components/ui/Btn";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

const KNOWN_FIRMS = [
  "Apex", "TopStep", "Bulenox", "Take Profit Trader",
  "MyFundedFutures", "Earn2Trade", "Uprofit", "Leeloo",
  "EdgeClear", "TradeDay", "Other",
];

const KNOWN_PLATFORMS = [
  "Rithmic", "Tradovate", "NinjaTrader", "Quantower",
  "TradeStation", "Sierra Chart", "Bookmap", "Finamark",
  "Jigsaw", "Other",
];

const inp = (extra = {}) => ({
  width: "100%",
  padding: "10px 12px",
  background: "#1a1d2e",
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  color: C.text,
  fontSize: 13,
  marginBottom: 8,
  boxSizing: "border-box",
  ...extra,
});

// ─────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────
export function OnboardingWizard({ userId, displayName, onComplete, onSkip, setView, fetchAccounts }) {
  const [step, setStep]       = useState(0);
  const [saving, setSaving]   = useState(false);
  const [tradeMode, setTradeMode] = useState(null); // "manual" | "csv"

  // Account form state
  const [acctName,    setAcctName]    = useState("");
  const [firmSelect,  setFirmSelect]  = useState("");
  const [firmCustom,  setFirmCustom]  = useState("");
  const [platSelect,  setPlatSelect]  = useState("");
  const [platCustom,  setPlatCustom]  = useState("");
  const [acctType,    setAcctType]    = useState("funded");
  const [balance,     setBalance]     = useState("50000");
  const [split,       setSplit]       = useState("80");
  const [acctError,   setAcctError]   = useState("");

  const firmValue = firmSelect === "Other" ? firmCustom : firmSelect;
  const platValue = platSelect === "Other" ? platCustom : platSelect;

  const markComplete = async () => {
    setSaving(true);
    try {
      await authFetch(`${SUPABASE_URL}/rest/v1/profiles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify({ id: userId, onboarding_completed: true }),
      });
    } catch (e) {
      console.error("[OnboardingWizard] markComplete failed:", e);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    await markComplete();
    onSkip?.();
  };

  const handleFinish = async (destination) => {
    await markComplete();
    await onComplete?.(destination);
  };

  const createAccount = async () => {
    setAcctError("");
    if (!acctName.trim()) return setAcctError("Account name is required");
    if (firmSelect === "Other" && !firmCustom.trim()) return setAcctError("Enter the prop firm name");
    if (platSelect === "Other" && !platCustom.trim()) return setAcctError("Enter the platform name");

    // Check for duplicate name against existing accounts
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/accounts?user_id=eq.${userId}&name=eq.${encodeURIComponent(acctName.trim())}&is_active=eq.true`
      );
      const existing = await res.json();
      if (Array.isArray(existing) && existing.length > 0) {
        return setAcctError("An account with this name already exists. Please use a different name.");
      }
    } catch (e) {
      // Non-blocking — proceed if check fails
    }

    setSaving(true);
    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/accounts`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify({
          user_id:          userId,
          name:             acctName.trim(),
          broker:           platValue.trim() || null,
          account_type:     acctType,
          starting_balance: parseFloat(balance) || 50000,
          prop_firm:        firmValue.trim() || null,
          payout_split:     parseFloat(split) / 100 || 0.80,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || err.error || "Failed to create account");
      }
      await fetchAccounts?.();
      setStep(2);
    } catch (e) {
      setAcctError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const STEPS = ["Welcome", "Your Account", "Log Trades", "Ready"];

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(8,10,20,0.97)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      zIndex: 9000, padding: 20,
      overflowY: "auto",
    }}>
      <div style={{
        width: "100%", maxWidth: 560,
        background: "#0f1322",
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        overflow: "visible",
        margin: "auto",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
      }}>

        {/* Progress bar */}
        <div style={{ height: 3, background: C.border }}>
          <div style={{
            height: "100%",
            width: `${((step + 1) / STEPS.length) * 100}%`,
            background: C.blue,
            transition: "width 0.3s ease",
          }} />
        </div>

        {/* Step indicators */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          padding: "16px 24px 0",
        }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{
              fontSize: 10, fontWeight: 600,
              color: i === step ? C.blue : i < step ? C.green : C.muted,
              textTransform: "uppercase", letterSpacing: "0.08em",
            }}>
              {i < step ? "✓ " : ""}{s}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: "28px 32px 32px", overflowY: "auto", flex: 1 }}>

          {/* ── STEP 0: WELCOME ── */}
          {step === 0 && (
            <div>
              <div style={{ fontSize: 36, marginBottom: 12 }}>👋</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: "0 0 8px" }}>
                Welcome{displayName ? `, ${displayName}` : ""}
              </h2>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
                Futures OS is a behavioral analysis system for prop traders. Here's what it does:
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
                {[
                  ["🔍", "Finds your leaks", "22 detectors analyze every trade and tell you exactly which behavioral patterns are costing you money — in dollars per month."],
                  ["🧠", "Coaches your psychology", "AI synthesis connects your emotional state, discipline score, and behavioral patterns into actionable coaching."],
                  ["📊", "Tracks your business", "Log payouts and expenses per prop firm to see your true ROI — not just trade P&L."],
                  ["📋", "Keeps your journal", "Every trade logged becomes a behavioral data point. The more you log, the smarter the analysis gets."],
                ].map(([icon, title, desc]) => (
                  <div key={title} style={{
                    display: "flex", gap: 14, padding: "14px 16px",
                    background: "#1a1d2e", borderRadius: 10,
                    border: `1px solid ${C.border}`,
                  }}>
                    <div style={{ fontSize: 20, flexShrink: 0 }}>{icon}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 3 }}>{title}</div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Btn onClick={() => setStep(1)} style={{ flex: 1 }}>
                  Get Started →
                </Btn>
                <button
                  onClick={handleSkip}
                  style={{
                    padding: "10px 16px", background: "none",
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    color: C.muted, fontSize: 13, cursor: "pointer",
                  }}
                >
                  Skip setup
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 1: CREATE ACCOUNT ── */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🏦</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: "0 0 6px" }}>
                Create your first account
              </h2>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
                Trades are linked to accounts. Start with your main funded account.
              </p>

              <input
                placeholder="Account name (e.g. Apex $50K #1)"
                value={acctName}
                onChange={e => setAcctName(e.target.value)}
                style={inp()}
                autoFocus
              />

              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Prop Firm</label>
              <select value={firmSelect} onChange={e => { setFirmSelect(e.target.value); setFirmCustom(""); }} style={inp()}>
                <option value="">— Select firm —</option>
                {KNOWN_FIRMS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              {firmSelect === "Other" && (
                <input
                  placeholder="Enter prop firm name"
                  value={firmCustom}
                  onChange={e => setFirmCustom(e.target.value)}
                  style={inp({ borderColor: C.blue })}
                  autoFocus
                />
              )}

              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Account Type</label>
              <select value={acctType} onChange={e => setAcctType(e.target.value)} style={inp()}>
                <option value="funded">Funded Account</option>
                <option value="prop">Prop Eval</option>
                <option value="live">Live Account</option>
                <option value="sim">Sim Account</option>
              </select>

              <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Platform</label>
              <select value={platSelect} onChange={e => { setPlatSelect(e.target.value); setPlatCustom(""); }} style={inp()}>
                <option value="">— Select platform —</option>
                {KNOWN_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              {platSelect === "Other" && (
                <input
                  placeholder="Enter platform name"
                  value={platCustom}
                  onChange={e => setPlatCustom(e.target.value)}
                  style={inp({ borderColor: C.blue })}
                />
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Starting Balance ($)</label>
                  <input type="number" value={balance} onChange={e => setBalance(e.target.value)} style={inp()} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>Your Payout Split (%)</label>
                  <input type="number" min={0} max={100} value={split} onChange={e => setSplit(e.target.value)} style={inp()} />
                </div>
              </div>

              {acctError && (
                <div style={{ fontSize: 12, color: C.red, marginBottom: 8, padding: "8px 12px", background: C.red + "15", borderRadius: 6 }}>
                  ⚠ {acctError}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <Btn onClick={createAccount} disabled={saving} style={{ flex: 1 }}>
                  {saving ? "Creating…" : "Create Account →"}
                </Btn>
                <button
                  onClick={() => setStep(2)}
                  style={{
                    padding: "10px 16px", background: "none",
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    color: C.muted, fontSize: 13, cursor: "pointer",
                  }}
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: HOW TO LOG TRADES ── (skipped if account created — goes straight to 3) */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: "0 0 6px" }}>
                How will you log trades?
              </h2>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>
                You can log trades manually one at a time, or import a CSV export from your broker or platform.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
                {[
                  {
                    id: "manual",
                    icon: "✍️",
                    title: "Log manually",
                    desc: "Enter each trade as you take it. Takes about 2 minutes per trade. Best for staying mindful of your behavior.",
                  },
                  {
                    id: "csv",
                    icon: "📂",
                    title: "Import from CSV",
                    desc: "Export your trade history from Rithmic, Tradovate, NinjaTrader, or similar and import it in bulk.",
                  },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setTradeMode(opt.id)}
                    style={{
                      display: "flex", gap: 14, padding: "16px",
                      background: tradeMode === opt.id ? C.blue + "15" : "#1a1d2e",
                      border: `2px solid ${tradeMode === opt.id ? C.blue : C.border}`,
                      borderRadius: 10, cursor: "pointer", textAlign: "left",
                      transition: "all 0.15s",
                    }}
                  >
                    <div style={{ fontSize: 24, flexShrink: 0 }}>{opt.icon}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{opt.title}</div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Btn
                  onClick={() => setStep(3)}
                  disabled={!tradeMode}
                  style={{ flex: 1, opacity: tradeMode ? 1 : 0.5 }}
                >
                  Continue →
                </Btn>
                <button
                  onClick={() => setStep(3)}
                  style={{
                    padding: "10px 16px", background: "none",
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    color: C.muted, fontSize: 13, cursor: "pointer",
                  }}
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: READY ── */}
          {step === 3 && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🚀</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: "0 0 10px" }}>
                One last step
              </h2>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 8, maxWidth: 400, margin: "0 auto 8px" }}>
                Activate your free trial through our payment processor. No card required — $0 due today. This just sets up your account for when your trial ends.
              </p>

              {/* Trust signals */}
              <div style={{
                display: "flex", justifyContent: "center", gap: 20,
                margin: "0 auto 24px", flexWrap: "wrap",
              }}>
                {["✓ No credit card", "✓ 30 days free", "✓ Cancel anytime"].map(t => (
                  <span key={t} style={{ fontSize: 11, color: C.green, fontFamily: "monospace" }}>{t}</span>
                ))}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 320, margin: "0 auto" }}>
                <Btn
                  onClick={() => handleFinish("billing")}
                  style={{ width: "100%" }}
                >
                  Activate Free Trial →
                </Btn>

                <button
                  onClick={() => handleFinish("dashboard")}
                  style={{
                    padding: "10px 16px", background: "none",
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    color: C.muted, fontSize: 13, cursor: "pointer",
                  }}
                >
                  Skip for now — go to Dashboard
                </button>

                <button
                  onClick={() => handleFinish("getting-started")}
                  style={{
                    padding: "8px 16px", background: "none",
                    border: "none", color: C.blue,
                    fontSize: 12, cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Read the Getting Started guide first
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
